// src/stores/ZoomStore.js - Zustand Store for Zoom Integration
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useZoomStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Connection status
    zoomConnection: null,
    zoomConnectionLoading: false,

    // OAuth flow state (handles redirect to Zoom)
    oauthUrl: null,
    oauthInitiating: false,

    // Disconnection
    disconnecting: false,

    // Connection state
    isConnected: false,

    // Cache state
    cache: {
      zoomConnection: null,
      lastFetch: null
    },

    // Internal flags
    _initialized: false,

    // ================================
    // SOCKET OPERATIONS (NO RETURN VALUES)
    // ================================

    /**
     * Load Zoom connection status - NO RETURN VALUE
     */
    loadZoomConnectionStatus: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ ZoomStore: Socket service not initialized');
        return;
      }

      console.log('🔌 ZoomStore: Loading connection status');
      set({
        zoomConnectionLoading: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_ZOOM_CONNECTION_STATUS, {});
    },

    /**
     * Start OAuth flow - generates OAuth URL
     * Frontend will redirect user to Zoom
     */
    startZoomOAuth: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ ZoomStore: Socket service not initialized');
        return;
      }

      console.log('🚀 ZoomStore: Starting OAuth flow');
      set({
        oauthInitiating: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.START_ZOOM_OAUTH, {});
    },

    /**
     * Disconnect Zoom account
     */
    disconnectZoom: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ ZoomStore: Socket service not initialized');
        return;
      }

      console.log('🔌 ZoomStore: Disconnecting Zoom');
      set({
        disconnecting: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.DISCONNECT_ZOOM, {});
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    /**
     * Check if Zoom account is connected
     */
    isZoomConnected: () => {
      const { zoomConnection } = get();
      return zoomConnection?.has_connection === true;
    },

    /**
     * Get connected account email
     */
    getAccountEmail: () => {
      const { zoomConnection } = get();
      return zoomConnection?.account_email || null;
    },

    /**
     * Check if token is expired
     */
    isTokenExpired: () => {
      const { zoomConnection } = get();
      return zoomConnection?.token_expired === true;
    },

    /**
     * Get connection status string
     */
    getConnectionStatus: () => {
      const { zoomConnection } = get();
      return zoomConnection?.status || 'disconnected';
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================

    _setZoomConnectionLoaded: (data) => {
      console.log('📡 ZoomStore: Connection status received');

      set(state => ({
        zoomConnection: data,
        zoomConnectionLoading: false,
        isConnected: data?.has_connection === true,
        cache: {
          ...state.cache,
          zoomConnection: data,
          lastFetch: new Date()
        }
      }));
    },

    _setOAuthUrlGenerated: (data) => {
      console.log('📡 ZoomStore: OAuth URL generated');

      set({
        oauthUrl: data.url,
        oauthInitiating: false
      });
    },

    _setZoomDisconnected: (data) => {
      console.log('📡 ZoomStore: Zoom disconnected');

      set({
        zoomConnection: null,
        disconnecting: false,
        isConnected: false,
        cache: {
          zoomConnection: null,
          lastFetch: null
        }
      });
    },

    // ================================
    // SOCKET CONNECTION MANAGEMENT (following CustomerStore pattern)
    // ================================

    _setConnectionState: (isConnected) => {
      set({ isConnected });
    },

    _getSocketManager: () => {
      // Access socket manager from window global (following pattern)
      if (typeof window !== 'undefined' && window.__socketManager) {
        return { socketManager: window.__socketManager };
      }
      return { socketManager: null };
    },

    _handleAuthReset: () => {
      console.log('🧹 ZoomStore: Resetting all state due to auth reset');

      set({
        // Reset all state to initial values
        zoomConnection: null,
        zoomConnectionLoading: false,
        oauthUrl: null,
        oauthInitiating: false,
        disconnecting: false,
        isConnected: false,
        cache: {
          zoomConnection: null,
          lastFetch: null
        },
        _initialized: false
      });
    },

    // ================================
    // INITIALIZATION (following CustomerStore pattern)
    // ================================

    _initialize: (socketManager) => {
      const state = get();

      console.log('🔌 ZoomStore: Initializing with socket manager');

      // Store socket manager reference
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false
      });

      // Auto-load Zoom connection status if authenticated
      if (socketManager?.isAuthenticated) {
        console.log('🔄 ZoomStore: Auto-loading Zoom connection status (first time)');
        state.loadZoomConnectionStatus();
      }
    }
  }))
);

// ================================
// SOCKET EVENT LISTENER INITIALIZATION
// ================================

/**
 * Initialize socket event listeners for ZoomStore
 * Called by SocketContext on auth success
 */
export const initializeZoomStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    console.error('❌ ZoomStore: Cannot initialize - socketManager not available');
    return;
  }

  console.log('🎧 ZoomStore: Initializing socket listeners');

  const store = useZoomStore.getState();

  // Mark as initialized
  useZoomStore.setState({ _initialized: true });

  // Central event handler
  const handleSocketEvent = (eventName, responseData) => {
    const store = useZoomStore.getState();

    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.ZOOM_CONNECTION_STATUS_LOADED:
        store._setZoomConnectionLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.ZOOM_OAUTH_URL_GENERATED:
        store._setOAuthUrlGenerated(responseData.data);
        break;

      case SOCKET_EVENTS.ZOOM_DISCONNECTED:
        store._setZoomDisconnected(responseData.data);
        break;

      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_ZOOM_CONNECTION_STATUS_FAILED:
        useZoomStore.setState({ zoomConnectionLoading: false });
        break;

      case SOCKET_EVENTS.START_ZOOM_OAUTH_FAILED:
        useZoomStore.setState({ oauthInitiating: false });
        break;

      case SOCKET_EVENTS.DISCONNECT_ZOOM_FAILED:
        useZoomStore.setState({ disconnecting: false });
        console.log('❌ ZoomStore: Disconnect failed -', responseData.message);
        break;

      default:
        console.warn('⚠️ ZoomStore: Unhandled event:', eventName);
    }
  };

  // Set up individual event listeners
  const zoomEvents = [
    // Success events
    SOCKET_EVENTS.ZOOM_CONNECTION_STATUS_LOADED,
    SOCKET_EVENTS.ZOOM_OAUTH_URL_GENERATED,
    SOCKET_EVENTS.ZOOM_DISCONNECTED,
    // Error events
    SOCKET_EVENTS.GET_ZOOM_CONNECTION_STATUS_FAILED,
    SOCKET_EVENTS.START_ZOOM_OAUTH_FAILED,
    SOCKET_EVENTS.DISCONNECT_ZOOM_FAILED
  ];

  zoomEvents.forEach(eventName => {
    socketManager.universalHandler.on(eventName, (data) => {
      handleSocketEvent(eventName, { event: eventName, data });
    });
  });

  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useZoomStore.getState()._handleAuthReset();
    });
  }

  // Initialize the store
  useZoomStore.getState()._initialize(socketManager);

  console.log('✅ ZoomStore: Socket listeners initialized');
};

export default useZoomStore;
